// Helpers that turn raw reservation numbers into plain language for the /admin
// page, so a volunteer never has to do the capacity-minus-reserved math in their
// head. Pure functions, unit-tested in reservationStatus.test.js.

/**
 * Describe the reservation state of one event.
 *
 * @param {object} status  { capacity, sold } as stored for the event
 * @returns {{state:string, seatsLeft:number|null, label:string, tone:string, over:boolean}}
 *   state: 'unlimited' | 'open' | 'almost' | 'full'
 *   tone:  'ok' | 'warn' | 'bad'  (drives the badge colour)
 */
export function summarizeReservation(status = {}) {
  const rawCapacity = status.capacity;
  const capacity =
    rawCapacity === null || rawCapacity === undefined || rawCapacity === ''
      ? null
      : Number(rawCapacity);
  const reserved = Number(status.sold) || 0;

  if (capacity === null || !Number.isFinite(capacity)) {
    return {
      state: 'unlimited',
      seatsLeft: null,
      label: 'Open — no seat limit set',
      tone: 'ok',
      over: false,
    };
  }

  const seatsLeft = Math.max(0, capacity - reserved);
  const over = reserved > capacity;

  if (seatsLeft <= 0) {
    return {
      state: 'full',
      seatsLeft: 0,
      label: 'FULL — nobody can reserve',
      tone: 'bad',
      over,
    };
  }
  if (seatsLeft <= 5) {
    return {
      state: 'almost',
      seatsLeft,
      label: `Almost full — ${seatsLeft} seat${seatsLeft === 1 ? '' : 's'} left`,
      tone: 'warn',
      over,
    };
  }
  return {
    state: 'open',
    seatsLeft,
    label: `Open — ${seatsLeft} seats left`,
    tone: 'ok',
    over,
  };
}

/**
 * Pull just the email addresses out of a pasted list, one per line.
 * Tolerates lines like `Jane Doe <jane@example.com>` or `Jane, jane@example.com`.
 */
export function cleanMemberList(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((line) => {
      const match = line.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/);
      return match ? match[1] : '';
    })
    .filter((email) => email.length > 0)
    .join('\n');
}

/**
 * Summarize a member list so the admin page can show what's actually in the box
 * before it is saved.
 *
 * @returns {{count:number, duplicates:number, unparsable:number}}
 *   count       - distinct email addresses found
 *   duplicates  - addresses listed more than once
 *   unparsable  - non-empty lines with no email address in them
 */
export function summarizeMemberList(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const seen = new Set();
  let duplicates = 0;
  let unparsable = 0;

  for (const line of lines) {
    const match = line.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/);
    if (!match) {
      unparsable += 1;
      continue;
    }
    const email = match[1].toLowerCase();
    if (seen.has(email)) duplicates += 1;
    else seen.add(email);
  }

  return { count: seen.size, duplicates, unparsable };
}
